import type { ApiResponse, PaginatedResponse } from '@/services/types'
import type { UserAccount, UserRole } from './auth'
export type { UserAccount, UserRole } from './auth'

// =================== Enums ===================

export type UserAccountStatus = 'normal' | 'disabled' | 'pending_deletion' | 'deleted'

// =================== Display Maps ===================

export const userRoleMap: Record<UserRole, string> = {
  super_admin: '超级管理员',
  admin: '系统管理员',
  leader: '单位领导',
  manager: '单位管理员',
  standard: '普通员工',
}

/** 下拉可分配角色(新建/变更角色用)— 与后端 UserAdminService.IMPORTABLE_ROLES 口径一致:super_admin 不可经 API 分配 */
export const assignableUserRoleOptions: { value: UserRole; label: string }[] = [
  { value: 'admin', label: '系统管理员' },
  { value: 'leader', label: '单位领导' },
  { value: 'manager', label: '单位管理员' },
  { value: 'standard', label: '普通员工' },
]

/** 角色筛选下拉选项 — 含 super_admin(后端列表筛选支持按超管筛选) */
export const userRoleFilterOptions: { value: UserRole; label: string }[] = [
  { value: 'super_admin', label: '超级管理员' },
  ...assignableUserRoleOptions,
]

export const userRoleTagType: Record<UserRole, 'danger' | 'warning' | 'primary' | 'info'> = {
  super_admin: 'danger',
  admin: 'danger',
  leader: 'warning',
  manager: 'primary',
  standard: 'info',
}

export const userStatusMap: Record<UserAccountStatus, string> = {
  normal: '正常',
  disabled: '禁用',
  pending_deletion: '注销中',
  deleted: '已注销',
}

export const userStatusTagType: Record<UserAccountStatus, 'success' | 'danger' | 'warning' | 'info'> = {
  normal: 'success',
  disabled: 'danger',
  pending_deletion: 'warning',
  deleted: 'info',
}

// =================== Entities ===================

export interface UserLoginRecord {
  id: string
  userId: string
  // 与后端 LoginAuditLog.method 对齐：sms_code/wechat/password；保留旧值兼容 mock
  loginMethod: 'sms_code' | 'wechat' | 'mobile_code' | 'wechat_qr' | 'wechat_mobile' | 'password'
  ipAddress: string
  deviceInfo?: string
  success: boolean
  createdAt: Date
}

// =================== DTOs / Params ===================

export interface CreateUserDto {
  mobile: string
  displayName: string
  role: UserRole
  organizationId?: string
}

export interface UpdateUserDto {
  displayName?: string
  mobile?: string
}

export interface UserListParams {
  keyword?: string
  role?: UserRole
  status?: UserAccountStatus
  organizationId?: string
  page?: number
  pageSize?: number
}

export interface UserStats {
  totalCount: number
  normalCount: number
  disabledCount: number
  /** 自主注销冷静期用户数(后端 df87e78 新增) */
  pendingDeletionCount?: number
  /** 已注销用户数(后端 df87e78 新增) */
  deletedCount?: number
  adminCount: number
  leaderCount: number
  managerCount: number
  standardCount: number
  monthlyNewCount: number
  unassignedCount: number
  activeCount: number
}

export interface BatchImportResult {
  total: number
  success: number
  failed: number
  errors: { row: number; reason: string }[]
}

export interface ResetPasswordResult {
  tempPassword: string
  forceChange: boolean
}

// =================== API Interfaces ===================

export interface UserAdminApi {
  listUsers(params: UserListParams): Promise<PaginatedResponse<UserAccount>>
  getUser(id: string): Promise<ApiResponse<UserAccount>>
  getUserStats(): Promise<ApiResponse<UserStats>>
  createUser(dto: CreateUserDto): Promise<ApiResponse<UserAccount & { tempPassword: string }>>
  updateUser(id: string, dto: UpdateUserDto): Promise<ApiResponse<UserAccount>>
  deleteUser(id: string): Promise<ApiResponse<void>>
  changeUserStatus(id: string, status: UserAccountStatus, reason: string): Promise<ApiResponse<UserAccount>>
  changeUserRole(id: string, role: UserRole, reason?: string): Promise<ApiResponse<UserAccount>>
  changeUserOrg(id: string, organizationId: string | null, reason?: string): Promise<ApiResponse<UserAccount>>
  resetUserPassword(id: string, forceChange?: boolean): Promise<ApiResponse<ResetPasswordResult>>
  forceLogout(userId: string): Promise<ApiResponse<void>>
  batchImportUsers(file: File, strategy: 'skip' | 'overwrite' | 'abort'): Promise<ApiResponse<BatchImportResult>>
  exportUsers(params: UserListParams): Promise<Blob>
}

export interface UserLoginRecordApi {
  listLoginRecords(userId: string, page?: number, pageSize?: number): Promise<PaginatedResponse<UserLoginRecord>>
}
